import { useState, useEffect } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { Loader2, Command, Mail } from 'lucide-react'
import { useAuthStore } from '@/stores/auth-store'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldSeparator,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'

// LocalStorage key for storing pending analysis ID to claim after signup
const PENDING_ANALYSIS_KEY = 'pending_analysis_id'

export default function LoginPage() {
  const [searchParams] = useSearchParams()
  const mode = searchParams.get('mode')

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isSignUp, setIsSignUp] = useState(() => mode === 'signup')
  const [localError, setLocalError] = useState<string | null>(null)
  const [confirmationMessage, setConfirmationMessage] = useState<string | null>(null)

  const { signIn, signUp, isAuthenticated, isLoading, error, clearError, onboardingCompleted } = useAuthStore()
  const navigate = useNavigate()
  
  // Get analysis ID from URL query params (passed from AnalysisResultsPage)
  const analysisId = searchParams.get('analysis')

  useEffect(() => {
    if (isAuthenticated) {
      if (!onboardingCompleted) {
        navigate('/onboarding')
      } else {
        navigate('/app/dashboard')
      }
    }
  }, [isAuthenticated, onboardingCompleted, navigate])

  // Sync form mode when URL mode param changes (e.g. /login vs /login?mode=signup)
  useEffect(() => {
    setIsSignUp(mode === 'signup')
  }, [mode])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLocalError(null)
    setConfirmationMessage(null)
    clearError()

    try {
      if (isSignUp) {
        await signUp(email, password)
      } else {
        await signIn(email, password)
      }
      
      // Store analysis ID in localStorage after successful auth
      // so the onboarding flow can claim it and pre-populate org data
      if (analysisId) {
        localStorage.setItem(PENDING_ANALYSIS_KEY, analysisId)
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : ''
      if (msg.toLowerCase().includes('check your email')) {
        setConfirmationMessage(msg)
      } else {
        setLocalError(msg)
      }
    }
  }

  return (
    <div className="bg-muted flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-sm md:max-w-4xl">
        <div className={cn("flex flex-col gap-6")}>
          <Card className="overflow-hidden p-0">
            <CardContent className="grid p-0 md:grid-cols-2">
              <form onSubmit={handleSubmit} className="p-6 md:p-8">
                <FieldGroup>
                  <div className="flex flex-col items-center gap-2 text-center">
                    <div className="flex aspect-square size-12 items-center justify-center rounded-lg bg-primary text-primary-foreground mb-2">
                      <Command className="size-6" />
                    </div>
                    <h1 className="text-2xl font-bold">
                      {isSignUp ? 'Create an account' : 'Welcome back'}
                    </h1>
                    <p className="text-muted-foreground text-balance">
                      {isSignUp 
                        ? 'Enter your email to create your Prospect Hunter account' 
                        : 'Login to your Prospect Hunter account'}
                    </p>
                  </div>

                  {confirmationMessage && (
                    <Alert className="border-green-500/50 text-green-600 dark:text-green-400 [&>svg]:text-green-600">
                      <Mail className="h-4 w-4" />
                      <AlertDescription>
                        {confirmationMessage}
                      </AlertDescription>
                    </Alert>
                  )}
                  
                  {(error || localError) && !confirmationMessage && (
                    <Alert variant="destructive">
                      <AlertDescription>
                        {error || localError}
                      </AlertDescription>
                    </Alert>
                  )}

                  <Field>
                    <FieldLabel htmlFor="email">Email</FieldLabel>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </Field>

                  <Field>
                    <div className="flex items-center">
                      <FieldLabel htmlFor="password">Password</FieldLabel>
                      {!isSignUp && (
                        <Link
                          to="/forgot-password"
                          className="ml-auto text-sm underline-offset-2 hover:underline"
                        >
                          Forgot your password?
                        </Link>
                      )}
                    </div>
                    <Input
                      id="password"
                      type="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                    />
                  </Field>

                  <Field>
                    <Button type="submit" className="w-full" disabled={isLoading}>
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {isSignUp ? 'Creating account...' : 'Signing in...'}
                        </>
                      ) : isSignUp ? (
                        'Create account'
                      ) : (
                        'Sign in'
                      )}
                    </Button>
                  </Field>

                  <FieldSeparator className="*:data-[slot=field-separator-content]:bg-card">
                    Or continue with
                  </FieldSeparator>

                  <Field className="grid grid-cols-2 gap-4">
                    <Button variant="outline" type="button">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                        <path
                          d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"
                          fill="currentColor"
                        />
                      </svg>
                      <span className="sr-only">Login with Apple</span>
                    </Button>
                    <Button variant="outline" type="button">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                        <path
                          d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
                          fill="currentColor"
                        />
                      </svg>
                      <span className="sr-only">Login with Google</span>
                    </Button>
                  </Field>

                  <FieldDescription className="text-center">
                    {isSignUp ? (
                      <>
                        Already have an account?{' '}
                        <Link
                          to={analysisId ? `/login?analysis=${analysisId}` : '/login'}
                          className="underline underline-offset-2 hover:text-primary"
                        >
                          Sign in
                        </Link>
                      </>
                    ) : (
                      <>
                        Don&apos;t have an account?{' '}
                        <Link
                          to={analysisId ? `/login?mode=signup&analysis=${analysisId}` : '/login?mode=signup'}
                          className="underline underline-offset-2 hover:text-primary"
                        >
                          Sign up
                        </Link>
                      </>
                    )}
                  </FieldDescription>
                </FieldGroup>
              </form>

              <div className="bg-muted relative hidden md:block">
                <img
                  src="https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&w=800&q=80"
                  alt="Business meeting"
                  className="absolute inset-0 h-full w-full object-cover dark:brightness-[0.2] dark:grayscale"
                />
              </div>
            </CardContent>
          </Card>

          <FieldDescription className="px-6 text-center">
            By clicking continue, you agree to our{' '}
            <a href="#" className="underline underline-offset-2 hover:text-primary">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="#" className="underline underline-offset-2 hover:text-primary">
              Privacy Policy
            </a>
            .
          </FieldDescription>
        </div>
      </div>
    </div>
  )
}
